'use client';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register once — safe to call multiple times
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export { gsap, ScrollTrigger };

/**
 * Animate a DOM element's textContent from 0 → endValue.
 *
 * @param {HTMLElement} el        Target element (from useRef)
 * @param {number}      endValue  Final numeric value
 * @param {object}      opts
 *   prefix   {string}  — prepended to number (e.g. '+', '−')
 *   suffix   {string}  — appended to number (e.g. '%', ' días', '×')
 *   decimals {number}  — decimal places for float values
 *   sep      {boolean} — thousands separator (e.g. 1,240)
 *   duration {number}  — animation duration in seconds (default 1.2)
 *   delay    {number}  — delay before start (default 0)
 *   ease     {string}  — GSAP ease (default 'power2.out')
 *   trigger  {Element} — if provided, uses ScrollTrigger (start: 'top 75%', once)
 */
export function countUp(el, endValue, opts = {}) {
  if (!el) return null;

  const {
    prefix = '',
    suffix = '',
    decimals = 0,
    sep = false,
    duration = 1.2,
    delay = 0,
    ease = 'power2.out',
    trigger,
  } = opts;

  const obj = { val: 0 };

  return gsap.to(obj, {
    val: endValue,
    duration,
    delay,
    ease,
    scrollTrigger: trigger
      ? { trigger, start: 'top 75%', once: true }
      : undefined,
    onUpdate() {
      let n;
      if (decimals > 0) {
        n = obj.val.toFixed(decimals);
      } else {
        n = Math.round(obj.val);
        if (sep) n = n.toLocaleString('es-CL');
      }
      el.textContent = `${prefix}${n}${suffix}`;
    },
  });
}

/**
 * Stagger entrance animation for a list of elements.
 *
 * @param {string|NodeList} selector  CSS selector or NodeList
 * @param {Element}         trigger   ScrollTrigger container element
 * @param {object}          opts
 *   y        {number} — initial y offset (default 30)
 *   stagger  {number} — stagger delay between items (default 0.08)
 *   duration {number} — per-item duration (default 0.5)
 *   scale    {number} — initial scale (optional)
 */
export function staggerIn(selector, trigger, opts = {}) {
  const { y = 30, stagger = 0.08, duration = 0.5, scale } = opts;
  const fromVars = { y, opacity: 0, duration, stagger, ease: 'power2.out' };
  if (scale !== undefined) fromVars.scale = scale;

  return gsap.from(selector, {
    ...fromVars,
    scrollTrigger: { trigger, start: 'top 78%', once: true },
  });
}

/**
 * Cross-fade panel content: fade out → swap content → fade in.
 * Returns a promise that resolves after the fade-out (use to setState in between).
 *
 * @param {HTMLElement} el  Panel container element
 */
export function panelFadeSwap(el) {
  return new Promise((resolve) => {
    gsap.to(el, {
      opacity: 0,
      duration: 0.15,
      ease: 'power1.in',
      onComplete: resolve,
    });
  });
}

export function panelFadeIn(el) {
  gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power1.out' });
}
